// src/components/Manual.js
import React, { useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import {
  IoHomeOutline, IoBarbellOutline, IoRestaurantOutline, IoCalendarOutline,
  IoTrophyOutline, IoTrendingUpOutline, IoChatbubbleOutline, IoBookOutline,
  IoPersonOutline, IoChevronDown, IoChevronUp, IoInformationCircleOutline,
  IoCheckmarkCircleOutline, IoStarOutline, IoPeopleOutline,
} from 'react-icons/io5';

const Section = ({ icon: Icon, title, children, color = 'text-marizan-red', defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass-panel rounded-2xl overflow-hidden">
      <button
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-white/5 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <div className={`w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0`}>
          <Icon size={20} className={color} />
        </div>
        <span className="flex-1 font-bold text-white text-base">{title}</span>
        {open ? <IoChevronUp size={18} className="text-gray-400 flex-shrink-0" /> : <IoChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/5 pt-4 text-sm text-gray-300 space-y-3 leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
};

const Step = ({ n, children }) => (
  <div className="flex gap-3">
    <div className="w-6 h-6 rounded-full bg-marizan-red/20 border border-marizan-red/30 text-marizan-red text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</div>
    <p className="flex-1">{children}</p>
  </div>
);

const Tip = ({ children }) => (
  <div className="flex gap-2 bg-yellow-900/20 border border-yellow-600/20 rounded-xl px-4 py-3">
    <IoStarOutline size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
    <p className="text-yellow-200 text-xs">{children}</p>
  </div>
);

// ── CONTENIDO COACH ──────────────────────────────────────────────
const CoachManual = () => (
  <div className="space-y-4">
    <Section icon={IoCheckmarkCircleOutline} title="Inicio rapido (Coach/Admin) — Primeros 15 minutos" defaultOpen>
      <p>Si es tu primera vez en la plataforma, sigue este orden para empezar sin perder tiempo:</p>
      <div className="space-y-2 mt-1">
        <Step n="1">Entra a <strong className="text-white">Clientes</strong> y crea o revisa tus clientes activos.</Step>
        <Step n="2">Ve a <strong className="text-white">Programas</strong> y deja listas tus plantillas principales (fuerza, hipertrofia, etc.).</Step>
        <Step n="3">Configura 1 o 2 <strong className="text-white">Planes de Nutricion</strong> base para tus perfiles mas comunes.</Step>
        <Step n="4">Asigna programa y nutricion desde la ficha del cliente.</Step>
        <Step n="5">Abre <strong className="text-white">Dashboard</strong> y verifica actividad, pendientes y adherencia.</Step>
      </div>
      <Tip>La opcion <strong className="text-white">Recordatorios</strong> se encuentra en el menu superior del usuario (avatar/nombre), no en la barra inferior.</Tip>
    </Section>

    <Section icon={IoHomeOutline} title="Dashboard — Vista general" defaultOpen>
      <p>El <strong className="text-white">Dashboard</strong> es tu panel de control. Al entrar verás:</p>
      <div className="space-y-2 mt-1">
        <Step n="1">Saludo personalizado con la fecha y tu nombre.</Step>
        <Step n="2">6 tarjetas de estadísticas: clientes activos, pendientes, logs de la semana, sesiones hoy, planes de nutrición y programas creados.</Step>
        <Step n="3">Una sección de <strong className="text-white">actividad reciente</strong> con los últimos logs de tus clientes.</Step>
        <Step n="4">El anillo de cumplimiento visual que muestra el porcentaje de adherencia global.</Step>
      </div>
      <Tip>Revisa el Dashboard cada mañana para saber qué clientes entrenaron ayer.</Tip>
    </Section>

    <Section icon={IoInformationCircleOutline} title="Licenciamiento de la plataforma (Admin/Coach)" color="text-cyan-400">
      <p>
        Esta aplicacion es licenciada por <strong className="text-white">Hugo Montero (gZy.Code)</strong>.
        El acceso de la plataforma depende de una licencia valida por servidor.
      </p>
      <div className="space-y-2 mt-1">
        <Step n="1"><strong className="text-white">Si vence la licencia:</strong> contacta al desarrollador para generar una nueva llave de activacion.</Step>
        <Step n="2"><strong className="text-white">Como renovarla:</strong> desde la pantalla de activacion copia el <strong className="text-white">App Key</strong> generado por la app y envialo al desarrollador.</Step>
        <Step n="3"><strong className="text-white">Respuesta del desarrollador:</strong> recibiras un string de licencia firmado para pegar en el campo de activacion.</Step>
        <Step n="4"><strong className="text-white">Importante:</strong> esa llave es unica para esa instancia/servidor y no puede transferirse a otra instalacion.</Step>
        <Step n="5"><strong className="text-white">Validacion:</strong> al activarse correctamente podras continuar al dashboard con normalidad.</Step>
      </div>
      <Tip>Nunca compartas la licencia activa fuera de tu servidor. Si migras de infraestructura, solicita una nueva licencia para la nueva instancia.</Tip>
    </Section>

    <Section icon={IoPeopleOutline} title="Clientes — Gestión completa" color="text-blue-400">
      <p>En <strong className="text-white">Clientes</strong> puedes:</p>
      <div className="space-y-2 mt-1">
        <Step n="1"><strong className="text-white">Crear un cliente:</strong> Nombre, email y contraseña inicial. Puedes activarlo o dejarlo pendiente.</Step>
        <Step n="2"><strong className="text-white">Ver su ficha completa:</strong> haz clic en cualquier cliente para ver su perfil, estadísticas, historial de logs y notas.</Step>
        <Step n="3"><strong className="text-white">Asignar programa:</strong> desde la ficha del cliente, botón «Asignar Programa».</Step>
        <Step n="4"><strong className="text-white">Asignar plan de nutrición:</strong> desde la ficha del cliente, botón «Asignar Nutrición».</Step>
        <Step n="5"><strong className="text-white">Activar / Desactivar:</strong> controla el acceso del cliente con un clic.</Step>
      </div>
      <Tip>Usa el buscador de la lista de clientes para encontrar a alguien rápido por nombre o email.</Tip>
    </Section>

    <Section icon={IoBookOutline} title="Programas — Crear y editar rutinas" color="text-green-400">
      <p>Crea programas de entrenamiento personalizados:</p>
      <div className="space-y-2 mt-1">
        <Step n="1"><strong className="text-white">Nuevo Programa:</strong> nombre, descripción, duración en semanas y días de entrenamiento (Lunes, Miércoles, Viernes, etc.).</Step>
        <Step n="2"><strong className="text-white">Modo de rutina:</strong> puedes elegir <strong className="text-white">Secuencial (Día A/B/C)</strong> o <strong className="text-white">Calendario (Lunes/Martes/...)</strong>.</Step>
        <Step n="3"><strong className="text-white">Auto-renombrar rutinas:</strong> botón rápido para ajustar nombres según el modo elegido.</Step>
        <Step n="4"><strong className="text-white">Grupos de ejercicios:</strong> organiza ejercicios en superseries, trisets o ejercicios simples.</Step>
        <Step n="5"><strong className="text-white">Layout intuitivo:</strong> columna izquierda para resumen/configuración y columna derecha para editar la rutina seleccionada.</Step>
        <Step n="6">Una vez creado, <strong className="text-white">asígna el programa</strong> a un cliente desde su ficha.</Step>
      </div>
      <Tip>Crea programas «plantilla» con nombres genéricos (ej. «Fuerza 4 semanas») para reutilizarlos con varios clientes.</Tip>
    </Section>

    <Section icon={IoRestaurantOutline} title="Nutrición — Planes y asignaciones" color="text-orange-400">
      <p>Gestiona la nutrición de tus clientes:</p>
      <div className="space-y-2 mt-1">
        <Step n="1"><strong className="text-white">Crear plan:</strong> nombre, objetivo (volumen, definición…), descripción.</Step>
        <Step n="2"><strong className="text-white">Días:</strong> añade los días del plan con su nombre (ej. «Lunes – Día de Entreno Alto Carbo»).</Step>
        <Step n="3"><strong className="text-white">Comidas:</strong> cada día puede tener desayuno, almuerzo, cena… con sus alimentos y macros.</Step>
        <Step n="4"><strong className="text-white">Alimentos:</strong> introduce nombre, cantidad, unidad y macronutrientes (kcal, proteína, carbos, grasa).</Step>
        <Step n="5">Asigna el plan al cliente desde su ficha. El cliente lo verá organizado por días.</Step>
      </div>
      <Tip>El campo de nombre del día acepta texto largo: «Lunes – Alto Carbo (Día de Entreno)» se mostrará completo.</Tip>
    </Section>

    <Section icon={IoBarbellOutline} title="Biblioteca de Ejercicios" color="text-purple-400">
      <div className="space-y-2">
        <Step n="1">Accede desde el menú lateral o <strong className="text-white">/exercises</strong>.</Step>
        <Step n="2">Crea ejercicios con nombre, categoría, descripción e imagen.</Step>
        <Step n="3">Añade un <strong className="text-white">vídeo de YouTube</strong> (URL) para que el cliente pueda ver la técnica.</Step>
        <Step n="4"><strong className="text-white">Subida de imágenes:</strong> además de URL, ahora puedes subir imagen directa (JPEG/PNG/WebP, límite 3 MB).</Step>
        <Step n="5">Los ejercicios que crees estarán disponibles al armar programas.</Step>
      </div>
    </Section>

    <Section icon={IoPeopleOutline} title="Administracion (solo Admin)" color="text-cyan-400">
      <div className="space-y-2">
        <Step n="1">Entra a <strong className="text-white">Gestionar Usuarios</strong> para crear, editar y cambiar roles.</Step>
        <Step n="2">Asigna estado de membresia (activo, inactivo, pendiente) segun tu operacion.</Step>
        <Step n="3">Monitorea salud basica del sistema y conteos globales desde panel admin.</Step>
        <Step n="4">Revisa la seccion <strong className="text-white">Info</strong> para ver licencia activa, cliente licenciado y dias restantes.</Step>
      </div>
      <Tip>Buenas practicas: limita cuentas admin a lo minimo necesario y usa cuentas coach para trabajo diario.</Tip>
    </Section>

    <Section icon={IoInformationCircleOutline} title="About / Info de la app" color="text-cyan-400">
      <div className="space-y-2">
        <Step n="1">En el menú superior de usuario encontrarás <strong className="text-white">Info</strong>.</Step>
        <Step n="2">Como coach verás estado general y métricas funcionales de la app.</Step>
        <Step n="3">El contenido técnico sensible no se muestra para mantener la seguridad.</Step>
      </div>
    </Section>

    <Section icon={IoTrophyOutline} title="Logros — Gestionar recompensas" color="text-yellow-400">
      <div className="space-y-2">
        <Step n="1">Accede a <strong className="text-white">Logros</strong> desde el menú.</Step>
        <Step n="2">Ve el catálogo completo: logros del sistema (automáticos) y tus logros personalizados.</Step>
        <Step n="3"><strong className="text-white">Crear logro:</strong> nombre, descripción, icono y condición (nº entrenos, mediciones, o manual).</Step>
        <Step n="4">Los logros con condición automática se desbloquean solos cuando el cliente cumple el objetivo.</Step>
        <Step n="5">Los logros manuales los otorgas tú desde la ficha del cliente.</Step>
      </div>
      <Tip>Los logros manuales como «Mejor del Mes» son un gran motivador. Úsalos con regularidad.</Tip>
    </Section>

    <Section icon={IoChatbubbleOutline} title="Mensajes" color="text-teal-400">
      <div className="space-y-2">
        <Step n="1">Accede a <strong className="text-white">Mensajes</strong> para comunicarte con tus clientes.</Step>
        <Step n="2">Selecciona un cliente de la lista y escribe tu mensaje.</Step>
        <Step n="3">Los mensajes son en tiempo real. El cliente los verá al abrir la app.</Step>
      </div>
    </Section>

    <Section icon={IoPersonOutline} title="Configuración de perfil" color="text-gray-400">
      <div className="space-y-2">
        <Step n="1">Haz clic en tu nombre en la barra superior o ve a <strong className="text-white">Perfil</strong>.</Step>
        <Step n="2">Actualiza tu nombre, email y contraseña.</Step>
        <Step n="3">Si cambias el email, usa el nuevo en el próximo inicio de sesión.</Step>
      </div>
    </Section>
  </div>
);

// ── CONTENIDO CLIENTE ────────────────────────────────────────────
const ClientManual = () => (
  <div className="space-y-4">
    <Section icon={IoCheckmarkCircleOutline} title="Inicio rapido (Cliente) — Tu primera semana" defaultOpen>
      <p>Para adaptarte rapido a la app, este es el flujo recomendado:</p>
      <div className="space-y-2 mt-1">
        <Step n="1">Abre <strong className="text-white">Entreno</strong> y completa tu sesion del dia registrando cada log.</Step>
        <Step n="2">Revisa <strong className="text-white">Nutricion</strong> y prepara tus comidas del siguiente dia.</Step>
        <Step n="3">Consulta <strong className="text-white">Calendario</strong> para anticipar tus proximos entrenamientos.</Step>
        <Step n="4">Registra una entrada en <strong className="text-white">Progreso</strong> cada 7 dias.</Step>
        <Step n="5">Usa <strong className="text-white">Mensajes</strong> para reportar dudas o ajustes a tu coach.</Step>
      </div>
      <Tip>Constancia > perfeccion. Si entrenaste, registra tu log aunque no haya sido una sesion perfecta.</Tip>
    </Section>

    <Section icon={IoHomeOutline} title="Dashboard — Tu resumen diario" defaultOpen>
      <p>Al abrir la app llegas al <strong className="text-white">Dashboard</strong>. Aquí verás:</p>
      <div className="space-y-2 mt-1">
        <Step n="1">La tarjeta de tu <strong className="text-white">entrenamiento de hoy</strong>: qué rutina te toca y cuántos ejercicios tiene.</Step>
        <Step n="2">Tu barra de progreso de la semana: cuántos días has entrenado.</Step>
        <Step n="3">Estadísticas rápidas: racha actual, total de entrenos, logros y más.</Step>
        <Step n="4">Accesos rápidos a las secciones más usadas.</Step>
      </div>
      <Tip>El punto amarillo en el calendario indica el día de hoy. Los puntos rojos son días con entrenamiento programado.</Tip>
    </Section>

    <Section icon={IoBarbellOutline} title="Mi Entrenamiento — Sesión de hoy" color="text-green-400">
      <div className="space-y-2">
        <Step n="1">Accede a <strong className="text-white">Entreno</strong> para ver la rutina del día.</Step>
        <Step n="2">Cada ejercicio muestra las series, repeticiones y el peso sugerido.</Step>
        <Step n="3"><strong className="text-white">Detalle rápido:</strong> toca el nombre del ejercicio para abrir un modal con técnica, video e imágenes.</Step>
        <Step n="4">También puedes abrir la página completa del ejercicio desde ese mismo bloque.</Step>
        <Step n="5">Usa el <strong className="text-white">temporizador</strong> (botón de reloj) para controlar los descansos.</Step>
        <Step n="6"><strong className="text-white">Registra tu log:</strong> introduce las repeticiones y el peso real que usaste.</Step>
        <Step n="7">Al guardar el log, se actualiza tu historial y pueden desbloquearse logros.</Step>
      </div>
      <Tip>Registra siempre tus logs aunque no hayas podido hacer el 100% — cada entrada cuenta para tus logros de constancia.</Tip>
    </Section>

    <Section icon={IoRestaurantOutline} title="Nutrición — Tu plan alimenticio" color="text-orange-400">
      <div className="space-y-2">
        <Step n="1">Accede a <strong className="text-white">Nutrición</strong> para ver el plan que te asignó tu coach.</Step>
        <Step n="2">Usa los botones de navegación ← → para ver los días anteriores o siguientes.</Step>
        <Step n="3">El día marcado con <span className="text-yellow-400">●</span> es el de hoy según el ciclo del plan.</Step>
        <Step n="4">Toca el nombre de una comida (ej. «Desayuno») para <strong className="text-white">expandirla</strong> y ver los alimentos y macros.</Step>
        <Step n="5">En la parte superior verás el total de kcal, proteínas, carbos y grasas del día.</Step>
      </div>
      <Tip>Consulta el plan la noche anterior para preparar tus comidas con antelación.</Tip>
    </Section>

    <Section icon={IoCalendarOutline} title="Calendario — Tu planificación semanal" color="text-blue-400">
      <div className="space-y-2">
        <Step n="1">El <strong className="text-white">Calendario</strong> muestra todos tus días de entrenamiento del programa.</Step>
        <Step n="2">Navega entre meses con las flechas ← →.</Step>
        <Step n="3">Los días marcados en rojo tienen sesión de entrenamiento asignada.</Step>
        <Step n="4">Haz clic en un día para ver qué rutina corresponde.</Step>
      </div>
    </Section>

    <Section icon={IoTrendingUpOutline} title="Mi Progreso — Seguimiento corporal" color="text-purple-400">
      <div className="space-y-2">
        <Step n="1">Accede a <strong className="text-white">Progreso</strong> para registrar tus medidas corporales.</Step>
        <Step n="2">Introduce peso, % grasa corporal, cintura, pecho, cadera, bícep, muslo.</Step>
        <Step n="3">Las gráficas muestran tu evolución en el tiempo.</Step>
        <Step n="4">Cada entrada que guardes puede desbloquear logros de seguimiento.</Step>
      </div>
      <Tip>Mídete siempre en las mismas condiciones: por la mañana, en ayunas y después de ir al baño, para datos consistentes.</Tip>
    </Section>

    <Section icon={IoTrophyOutline} title="Mis Logros — Recompensas y motivación" color="text-yellow-400">
      <div className="space-y-2">
        <Step n="1">Accede a <strong className="text-white">Logros</strong> para ver todas las recompensas disponibles.</Step>
        <Step n="2">Los logros en color <span className="text-yellow-400">dorado</span> ya los has desbloqueado.</Step>
        <Step n="3">Los logros en gris muestran qué necesitas hacer para desbloquearlos.</Step>
        <Step n="4">Algunos logros son otorgados directamente por tu coach (ej. «Mejor del Mes»).</Step>
        <Step n="5">La barra de progreso en la parte superior muestra cuántos logros has conseguido.</Step>
      </div>
      <Tip>Revisa los logros bloqueados para saber qué objetivos perseguir esta semana.</Tip>
    </Section>

    <Section icon={IoChatbubbleOutline} title="Mensajes — Comunicación con tu coach" color="text-teal-400">
      <div className="space-y-2">
        <Step n="1">Abre <strong className="text-white">Mensajes</strong> para hablar con tu coach.</Step>
        <Step n="2">Escribe tu consulta, duda o feedback y envíalo.</Step>
        <Step n="3">Tu coach te responderá en la misma conversación.</Step>
      </div>
      <Tip>No dudes en preguntar a tu coach sobre técnica, nutrición o ajustes al programa. ¡Para eso está!</Tip>
    </Section>

    <Section icon={IoPersonOutline} title="Mi Perfil" color="text-gray-400">
      <div className="space-y-2">
        <Step n="1">Accede a tu perfil desde el menú superior (tu nombre o avatar).</Step>
        <Step n="2">Actualiza tu datos personales: peso, altura, fecha de nacimiento.</Step>
        <Step n="3">En <strong className="text-white">Preferencias</strong> puedes cambiar tu contraseña cuando quieras.</Step>
      </div>
    </Section>
  </div>
);

// ── MAIN COMPONENT ───────────────────────────────────────────────
const Manual = () => {
  const { user } = useAuthContext();
  const isCoach = ['coach', 'admin'].includes(user?.role);

  return (
    <div className="w-full container mx-auto p-4 md:p-8 text-white animate-fade-up max-w-3xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-marizan-red/20 border border-marizan-red/30 flex items-center justify-center">
            <IoInformationCircleOutline size={22} className="text-marizan-red" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold">Manual de uso</h1>
        </div>
        <p className="text-gray-400">
          {isCoach
            ? 'Guia completa para coach/admin con pasos recomendados de operacion diaria.'
            : 'Guia completa para clientes con flujo practico de uso diario y semanal.'}
        </p>
      </div>

      {/* Quick tips banner */}
      <div className="glass-panel rounded-2xl p-4 mb-6 flex items-start gap-3">
        <IoCheckmarkCircleOutline size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-300">
          Toca cualquier sección para <strong className="text-white">expandirla</strong> y ver los detalles.
          Sigue primero la seccion de <strong className="text-white">Inicio rapido</strong> y luego revisa el resto por modulo.
        </p>
      </div>

      {isCoach ? <CoachManual /> : <ClientManual />}
    </div>
  );
};

export default Manual;
